<template>
    <div
        class="d-flex flex-column flex-fill gap-2"
        ref="content"
    >
        <FormLayout :label-width="120">
            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel>{{ $tm('station.name') }}</FormLayoutLabel>

                    <InputText
                        ref="nameInputEditor"
                        class="flex-fill"
                        v-model="station!.Name"
                        :validator="new ValidatorDef({ Required: true, Max: null, Min: null })"
                    />
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel tooltip="station.backupfortooltip">{{ $tm('station.backupfor') }}</FormLayoutLabel>

                    <InputSelect
                        :datasource="internalStationDatasource"
                        v-model="station!.BackupForStationID"
                    />
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel>{{ $tm('station.functiontype') }}</FormLayoutLabel>

                    <InputSelect
                        :datasource="internalStationTypeDatasource"
                        v-model="station!.StationTypeID"
                    />
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow>
                <FormLayoutLabel></FormLayoutLabel>
                <InputBool
                    v-model="station!.UseUsedTime"
                    tooltip="station.stationuseusedtimetooltip"
                    label="station.stationuseusedtime"
                    @change="onUseUsedTimeChanged"
                />
            </FormLayoutRow>

            <Transition name="fade-inout">
                <FormLayoutRow v-if="useUsedTimeChecked">
                    <FormLayoutLabel></FormLayoutLabel>
                    <div class="d-flex flex-nowrap">
                        <div
                            class="ms-3 me-2 border border-end-0 border-top-0"
                            style="height: 12px; width: 15px"
                        ></div>
                        <InputBool
                            class="flex-fill"
                            v-model="station!.UsedTimeSetsStarttime"
                            tooltip="station.stationusedtimesetsstarttimetooltip"
                            label="station.stationusedtimesetsstarttime"
                        />
                    </div>
                </FormLayoutRow>
            </Transition>

            <FormLayoutRowSeparator text="station.stationalias" />

            <StationAliasListView
                ref="stationAliasListView"
                v-model="station!.StationAliases"
                :id="station!.ID"
                :customColumns="handleStationAliasColumns"
            />

            <FormLayoutRow class="my-5"><div></div></FormLayoutRow>

            <FormLayoutRowSeparator
                class="my-5"
                text="station.contactperson"
            />

            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel>{{ $tm('station.responsiblename') }}</FormLayoutLabel>

                    <div class="d-flex flex-fill">
                        <InputText
                            class="flex-fill"
                            v-model="station!.ResponsibleName"
                        />
                    </div>
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel>{{ $tm('station.responsiblephone') }}</FormLayoutLabel>

                    <InputPhone v-model="station!.ResponsiblePhone" />
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50">
                    <FormLayoutLabel>{{ $tm('station.responsibleemail') }}</FormLayoutLabel>

                    <div class="d-flex flex-fill">
                        <InputEmail v-model="station!.ResponsibleEmail" />
                    </div>
                </div>
                <div class="d-md-flex flex-fill align-items-sm-center w-md-50"></div>
            </FormLayoutRow>

            <FormLayoutRow class="my-5"><div></div></FormLayoutRow>
        </FormLayout>
    </div>
</template>
<script setup lang="ts">
import { InputBool, InputEmail, InputPhone, InputSelect, InputText } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow, FormLayoutRowSeparator } from '@/components/Form';
import { StationAlias, type StationDetails } from '@/objects/generated';
import { ValidatorDef, type IEditorComponent } from '@/objects/gui';
import StationAliasListView from '../StationAlias/StationAliasListView.vue';
import { useCommonStoreRef, useEventCommonStoreRef } from '@/stores';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { propertyof } from '@/utilities';
import { ref, computed } from 'vue';
import { FieldAlertMessage } from '@/objects';
import { useLoaders, useTaskLoader } from '@/composeables';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import StationAliasTimeshiftCellRenderer from './StationAliasTimeshiftCellRenderer.vue';

const { AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['STATIONS', 'STATIONTYPES']);

const { StationsWithNoneAsSelectLine } = useEventCommonStoreRef();
const { StationTypeAsSelectLine: StationTypesAsSelectLine } = useCommonStoreRef();

/**
 * The datasource for the station select
 */
const internalStationDatasource = computed(() => {
    return {
        mapsource: StationsWithNoneAsSelectLine.value,
    };
});

/**
 * The datasource for the station type select
 */
const internalStationTypeDatasource = computed(() => {
    return {
        mapsource: StationTypesAsSelectLine.value,
    };
});

/**
 * The v-model for the station
 */
const station = defineModel<StationDetails>({ required: true });

// Ref to the nameInput. We need this because it has a validator
const nameInputEditor = ref<IEditorComponent | null>(null);

// The ref to the listview of the Alias. We use this one to get the valid state of the list
const stationAliasListView = ref<InstanceType<typeof StationAliasListView> | null>(null);

/**
 * This one manipulates what columns we show in the Alias list. Since we dont need to show everything from the autogenerated GUIDefinition
 * @param columns
 */
const handleStationAliasColumns = (columns: GridColumnOptions<StationAlias>[]) => {
    // We remove the one with dataField = StationID from the columns. We dont need to show this one since we know the station
    columns = columns.filter((col) => col.dataField != propertyof<StationAlias>('StationID'));

    // Iterate the columns and disable sort and filter
    columns.forEach((col) => {
        col.sortable = false;
        col.filterable = false;
    });

    // We create a new column and add to the end of the columns
    let col = new GridColumnOptionsBuilder<StationAlias>(propertyof<StationAlias>('Timeshift'), 'station.timeshift')
        .ReadOnly()
        .SortDisabled()
        .CellRenderer(StationAliasTimeshiftCellRenderer)
        .Build();

    columns.push(col);

    return columns;
};

const useUsedTimeChecked = computed(() => station.value.UseUsedTime ?? false);

/**
 * Handles the change of the UseUsedTime checkbox. If this is unchecked we need to make sure the UsedTimeSetsStarttime is also unchecked
 * @param checked
 */
const onUseUsedTimeChanged = (checked: boolean) => {
    if (checked == false) {
        station.value.UsedTimeSetsStarttime = false;
    }
};

const computedMessages = computed(() => {
    let messages: FieldAlertMessage[] = [];

    if (nameInputEditor.value?.validateMessage != null) {
        let msg = new FieldAlertMessage(nameInputEditor.value.validateMessage.message);
        msg.alertMessage = nameInputEditor.value.validateMessage;
        msg.translationKey = nameInputEditor.value.validateMessage.message;
        messages.push(msg);
    }

    if (stationAliasListView.value != null && stationAliasListView.value.IsValid == false) {
        let msg = new FieldAlertMessage('stationalias.invalidlist', 'stationalias.invalidlist', 'danger');
        messages.push(msg);
    }

    return messages;
});

/**
 * Get the messages from the detail editors and expose them
 */
defineExpose({
    messages: computedMessages,

    canSave: computed(() => computedMessages.value.length == 0 && stationAliasListView.value?.CanSave === true),
});
</script>
